import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { FiltersProvider, useFilters } from '../../context/FiltersContext';
import Sidebar from './Sidebar';
import Header from './Header';
import FiltersSidebar from './FiltersSidebar';
import EmailVerificationBanner from '../EmailVerificationBanner';
import './DashboardLayout.css';

// Inner component that uses FiltersContext
function DashboardLayoutInner({ tagPreference, onTagPreferenceChange, tagStylePreference, onTagStylePreferenceChange }) {
  const { currentUser } = useAuth();
  const { darkMode, setDarkMode } = useTheme();
  const location = useLocation();
  const { isCollapsed: filtersCollapsed, filterOptions, activeFilters, filterCallbacks } = useFilters();

  // Deux états possibles: 'open', 'collapsed'
  const [sidebarState, setSidebarState] = useState(() => {
    if (typeof window === 'undefined') {
      return 'open';
    }
    const stored = window.localStorage.getItem('dashboard.sidebarState');
    return stored === 'collapsed' ? 'collapsed' : 'open';
  });

  // État pour le panneau de customization
  const [customizeOpen, setCustomizeOpen] = useState(false);

  // État pour les informations du header (résultats et filtres)
  const [headerInfo, setHeaderInfo] = useState({
    resultsText: '',
    activeFilters: []
  });

  // Get current page name for filter data
  const getCurrentPage = () => {
    if (location.pathname.includes('/dashboard/database')) return 'database';
    if (location.pathname.includes('/dashboard/conferences')) return 'conferences';
    if (location.pathname.includes('/dashboard/patents')) return 'patents';
    return null;
  };

  const currentPage = getCurrentPage();
  const showFilters = currentPage !== null;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('dashboard.sidebarState', sidebarState);
    }
  }, [sidebarState]);

  const toggleSidebar = () => {
    // Alterne uniquement entre 'open' et 'collapsed'
    setSidebarState((prevState) => (prevState === 'open' ? 'collapsed' : 'open'));
  };

  const toggleCustomize = () => {
    setCustomizeOpen((prev) => !prev);
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  // Handler for removing individual filters from header
  const handleRemoveFilter = (filterKey, value, chip) => {
    const callbacks = filterCallbacks[currentPage];
    if (!callbacks || !callbacks.onFilterChange) return;

    // Non-list filters (score floors, date ranges) declare the value to fall back to
    if (chip && Object.prototype.hasOwnProperty.call(chip, 'resetValue')) {
      callbacks.onFilterChange(filterKey, chip.resetValue);
      return;
    }

    // Get current active filters for this filter key
    const currentFilters = activeFilters[currentPage] || {};
    const currentValues = currentFilters[filterKey] || [];

    // Remove the specific value
    const newValues = Array.isArray(currentValues)
      ? currentValues.filter(v => v !== value)
      : [];

    // Update the filter with the new array
    callbacks.onFilterChange(filterKey, newValues);
  };

  return (
    <div className={`dashboard-layout sidebar-${sidebarState} ${sidebarState === 'collapsed' ? 'sidebar-collapsed' : ''} ${showFilters ? (filtersCollapsed ? 'filters-collapsed' : 'filters-open') : ''}`}>
      <Sidebar
        user={currentUser}
        state={sidebarState}
        onToggleSidebar={toggleSidebar}
        onToggleCustomize={toggleCustomize}
        onToggleDarkMode={toggleDarkMode}
      />

      {/* FiltersSidebar - only shown on pages that need it */}
      {showFilters && (
        <FiltersSidebar
          currentPage={currentPage}
          options={filterOptions[currentPage] || {}}
          activeFilters={activeFilters[currentPage] || {}}
          onFilterChange={filterCallbacks[currentPage]?.onFilterChange}
          onResetAllFilters={filterCallbacks[currentPage]?.onResetAllFilters}
          onSaveCurrentFilters={filterCallbacks[currentPage]?.onSaveCurrentFilters}
          savingCurrentFilter={filterCallbacks[currentPage]?.savingCurrentFilter}
        />
      )}

      <div className="main-content">
        {/* Email Verification Banner - Shows for unverified emails */}
        <EmailVerificationBanner />

        {/* Trial Badge now integrated in Header */}
        <Header
          user={currentUser}
          sidebarState={sidebarState}
          onToggleSidebar={toggleSidebar}
          tagPreference={tagPreference}
          onTagPreferenceChange={onTagPreferenceChange}
          tagStylePreference={tagStylePreference}
          onTagStylePreferenceChange={onTagStylePreferenceChange}
          customizeOpen={customizeOpen}
          onCustomizeOpenChange={setCustomizeOpen}
          resultsText={headerInfo.resultsText}
          activeFilters={headerInfo.activeFilters}
          onRemoveFilter={handleRemoveFilter}
        />
        <div className="content-wrapper">
          <Outlet context={{
            tagPreference,
            onTagPreferenceChange,
            tagStylePreference,
            onTagStylePreferenceChange,
            setHeaderInfo
          }} />
        </div>
      </div>
    </div>
  );
}

// Wrapper with FiltersProvider
function DashboardLayout(props) {
  return (
    <FiltersProvider>
      <DashboardLayoutInner {...props} />
    </FiltersProvider>
  );
}

export default DashboardLayout;
